import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  canonicalShiftCorrectionStatus,
  shiftCorrectionStatusVisual,
} from "./ShiftCorrectionStatusCards";
import {
  formatShiftDateRange,
  formatAppliedDateTime,
  formatScheduleDateDisplay,
} from "./shiftCorrectionApproveHelpers";
import { scheduleRequestTypeMeta } from "pages/MyZone/MyZoneApply/ShiftCorrection/shiftCorrectionHelpers";

const detailAccent = {
  date: detailAccentPalette.indigo,
  originalShift: detailAccentPalette.blue,
  newShift: detailAccentPalette.green,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
};

export const ShiftCorrectionDetailPanel = ({
  open,
  onClose,
  record,
  checkAccess,
  onApprove,
  onReject,
  onCancel,
  actionLoading = false,
  expanded = false,
  onToggleExpand,
}) => {
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  // No dedicated cancel-an-approved-shift-change endpoint exists yet, same
  // gap as Comp Off (see CompOffDetailPanel) — the parent's onCancel reuses
  // the same approve/reject endpoint with status: "Cancelled" as a first
  // attempt, needs verifying against the real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { userScreenAccess } = useSelector((state) => state.tokenReducer);
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel used to be gated
  // by plain editor permission (checkAccess prop, from index.js's
  // check_access). Now requires real approver permission (either level
  // counts). Note: the backend's Approve catalog has no "Shift Change"
  // submodule at all yet (flagged separately), so this reads as "no
  // access" for every role until that's added on the backend side — same
  // gap, not a new one introduced here. Unlike every other Approve
  // screen's detail panel, the no-access case here used to fail silently
  // (no showToast at all) — the toast calls below are new, added for
  // consistency with the rest.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Schedule Change",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Schedule Change",
    ) ||
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Shift Change",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Shift Change",
    );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur", defaultValues: { rejected_reason: "" } });

  useEffect(() => {
    setRejectDialogOpen(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    reset({ rejected_reason: "" });
  }, [record, reset]);

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) || "Atleast one alphabet required",
      },
    });
  }, [register]);

  const statusFlowData = useMemo(
    () => ({
      ...(record || {}),
      applied_by:
        record?.applied_by ||
        record?.employee_name ||
        record?.username ||
        record?.requested_by,
      applied_on: formatAppliedDateTime(
        record?.applied_on ||
          record?.applied_date ||
          record?.created_at ||
          record?.created_date ||
          record?.date,
      ),
      approved_by: record?.approved_by,
      approved_at: formatAppliedDateTime(
        record?.approved_at || record?.approved_on,
      ),
      rejected_by: record?.rejected_by,
      rejected_at: formatAppliedDateTime(
        record?.rejected_at || record?.rejected_on,
      ),
      cancelled_by: record?.cancelled_by || record?.canceled_by,
      cancelled_at: formatAppliedDateTime(
        record?.cancelled_at ||
          record?.cancelled_on ||
          record?.canceled_at ||
          record?.canceled_on,
      ),
      cancelled_reason:
        record?.cancelled_reason ||
        record?.cancel_reason ||
        record?.canceled_reason,
    }),
    [record],
  );

  if (!record) return null;

  const status = canonicalShiftCorrectionStatus(record?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling a shift change request even after
  // it has been Approved (not just while Pending), same as Comp Off.
  const canCancelApproved = status === "Approved";
  const employeeName = record?.employee_name || "N/A";
  const employeeId = record?.employee_id || record?.emp_id || "-";
  const department = record?.department_name || record?.department || "-";
  const designation = record?.designation_name || record?.designation || "-";
  const requestReason = record?.reason || record?.description || "-";
  // rejected_reason is the one shared reason column the backend also writes
  // a cancellation reason into -- the label must track actual status
  // instead of always reading "Reject Reason", matching the convention used
  // by OnDuty/CompOff/Permission/OverTime's detail panels for the same
  // model shape.
  const rejectedReason = record?.rejected_reason || "";
  const decisionReasonLabel =
    status === "Cancelled" ? "Cancel Reason" : "Reject Reason";
  const isWeekOffSwap = record?.request_type === "week_off_swap";
  const requestTypeMeta = scheduleRequestTypeMeta(record?.request_type);
  const requestTitle = isWeekOffSwap
    ? "Week Off Swap Request"
    : "Schedule Change Request";

  const handleCloseAction = () => onClose?.();

  const handleApproveClick = () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this schedule change request",
      );
      return;
    }
    onApprove?.(record);
  };

  const submitReject = (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this schedule change request",
      );
      return;
    }
    onReject?.(record, data?.rejected_reason);
    setRejectDialogOpen(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this schedule change request",
      );
      return;
    }
    onCancel?.(record, trimmed);
    setCancelDialogOpen(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  // Reject/Approve/Cancel below used to always render, only disabled (via
  // AccessGatedAction) when hasApproverAccess was false — leaving a
  // bordered footer strip with unusable buttons for an editor-only viewer.
  // Each branch now resolves to real content or a genuine `null` (same fix
  // as OverTime/DetailPanel.js) so GlobalSidePanel's own footer border only
  // draws when there's actually something to show.
  const footerContent = isPending ? (
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          disabled={actionLoading}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          disabled={actionLoading}
          loading={actionLoading}
          onClick={handleApproveClick}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Schedule Change
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Schedule Change Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", { type: "required", message: "Reject reason is required" });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={actionLoading}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) => setValue("rejected_reason", value, { shouldValidate: true })}
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this schedule change request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Schedule Change Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={actionLoading}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved schedule change"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This schedule change request has already been approved. Enter a reason
        to cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title={requestTitle}
        subtitle="Request details and status"
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Schedule Change", onClick: handleCloseAction },
          employeeName || requestTitle,
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <RequestDetailHeader
            userDetails={{
              image_url: record?.image_url,
              employee_id: employeeId,
              user_name: employeeName,
              department_name: department,
              designation,
            }}
            status={status}
            colorCode={record?.color_code || shiftCorrectionStatusVisual(status).accent}
          />

          <Mui.Box sx={detailCardSx}>
            <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
              Request Details
            </Mui.Typography>
            <RequestDetailField
              icon={CompareArrowsIcon}
              label="Type"
              value={requestTypeMeta.label}
              accent={detailAccent.originalShift}
            />
            <RequestDetailField
              icon={CalendarMonthIcon}
              label={isWeekOffSwap ? "Date(s)" : "Date"}
              value={isWeekOffSwap ? formatScheduleDateDisplay(record) : formatShiftDateRange(record?.date)}
              accent={detailAccent.date}
            />
            <RequestDetailField
              icon={WorkOutlineIcon}
              label="Original Shift"
              value={record?.original_shift || "-"}
              accent={detailAccent.originalShift}
            />
            <RequestDetailField
              icon={CompareArrowsIcon}
              label="New Shift"
              value={record?.corrected_shift || "-"}
              accent={detailAccent.newShift}
            />
            <RequestDetailField
              icon={DescriptionOutlinedIcon}
              label="Reason"
              value={requestReason}
              accent={detailAccent.reason}
              multiline
              isLast={!rejectedReason}
            />
            {rejectedReason ? (
              <RequestDetailField
                icon={DescriptionOutlinedIcon}
                label={decisionReasonLabel}
                value={rejectedReason}
                accent={detailAccent.rejectReason}
                multiline
                isLast
              />
            ) : null}
          </Mui.Box>

          <RequestStatusTimeline
            endDate={[record?.date, record?.swap_date]}
            data={statusFlowData}
            variant="stepper"
            hidePendingStep
          />
          <ApprovalEditAuditTrail data={record} />
        </Mui.Box>
      </GlobalSidePanel>
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
